import { useState } from "react";
import { useJson, nf, ago, card, sectionLabel, Chip, ConfidenceBadge, victimPath, sectorPath, walletPath, Box } from "./shared";
import { StixBar } from "./Stix";

// Ransomware victims: a page per claimed victim, a page per sector, and a group's
// ransom notes and leaked negotiation chats (shown as tabs on its profile).

const fmtDay = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00Z` : d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const money = (n) => (n == null ? "—" : n >= 1e6 ? `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${n}`);
const groupHref = (g) => (g?.link ? `#/kb/e/${encodeURIComponent(g.link)}` : null);

function Fact({ k, children, ui }) {
  return <span style={{ minWidth: 0, wordBreak: "break-word", fontSize: ui.fs(12) }}><span style={{ color: ui.B.textDim }}>{k}: </span><span style={{ color: ui.B.text }}>{children}</span></span>;
}

function CopyText({ text, ui }) {
  const [copied, setCopied] = useState(false);
  const { B, fs, HP } = ui;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
      <code style={{ flex: "1 1 260px", minWidth: 0, fontSize: fs(11), color: B.textMuted, background: B.bg, border: `1px solid ${B.border}`, borderRadius: 6, padding: "6px 9px", wordBreak: "break-all" }}>{text}</code>
      <button onClick={() => navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })} style={{ ...HP.navBtn, padding: "5px 11px", fontSize: fs(11) }}>{copied ? "Copied ✓" : "Copy"}</button>
    </div>
  );
}

function VictimLine({ v, ui, showGroup }) {
  const { B, fs } = ui;
  return (
    <div style={{ display: "flex", gap: 10, padding: "7px 0", fontSize: fs(12), alignItems: "baseline", flexWrap: "wrap" }}>
      <a href={victimPath(v.vid)} style={{ flex: "1 1 200px", minWidth: 0, color: B.text, textDecoration: "none", fontWeight: 600, wordBreak: "break-word" }}>{v.title || "Unnamed"}</a>
      {showGroup && v.group && <span style={{ color: B.textDim }}>by {groupHref(v.group) ? <a href={groupHref(v.group)} style={{ color: B.textMuted }}>{v.group.label}</a> : v.group.label}</span>}
      <span style={{ color: B.textDim, whiteSpace: "nowrap" }}>{[v.country?.name || (typeof v.country === "string" ? v.country : null), v.sector && v.sector !== "Unknown" ? v.sector : null, fmtDay(v.date)].filter(Boolean).join(" · ")}</span>
    </div>
  );
}

export function KbVictim({ vid, ui }) {
  const { B, fs, HP, TypeIcon, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/victim/${encodeURIComponent(vid)}`);
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ color: "#dc2626", fontSize: fs(13) }}>{error}</div>;
  const { victim: v, group: g, elsewhere, groupRecent } = data;
  const color = getConf("identity").color;
  const site = v.website ? v.website.replace(/^https?:\/\//i, "").replace(/\/.*$/, "") : null;
  const btn = { ...HP.navBtn, padding: "7px 13px", fontSize: fs(12), textDecoration: "none", display: "inline-flex", alignItems: "center" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/victims" style={{ color: B.textDim, textDecoration: "none" }}>Ransomware Victims</a> / {v.title}</div>

      <div style={{ ...card(ui), borderTop: "3px solid #dc2626", padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TypeIcon type="identity" color={color} size={22} /></div>
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <div style={{ fontSize: fs(11), color: "#dc2626", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2 }}>Claimed ransomware victim</div>
            <h1 style={{ fontSize: fs(28), fontWeight: 900, margin: "2px 0 6px", color: B.text, wordBreak: "break-word" }}>{v.title || "Unnamed victim"}</h1>
            <div style={{ fontSize: fs(13), color: B.textMuted }}>
              Claimed by {g ? (groupHref(g) ? <a href={groupHref(g)} style={{ color: B.text, fontWeight: 800 }}>{g.label}</a> : <b>{g.label}</b>) : "an unknown group"} on its leak site{v.published || v.date ? `, ${fmtDay(v.published || v.date)}` : ""}.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {g?.ransomwareLive && <a href={g.ransomwareLive} target="_blank" rel="noreferrer noopener" style={btn}>ransomware.live ↗</a>}
            <a href={data.newsSearch} target="_blank" rel="noreferrer noopener" style={btn}>News coverage ↗</a>
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px 22px", flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: `1px solid ${B.border}`, alignItems: "center" }}>
          {v.sector && v.sector !== "Unknown" && <Fact k="Sector" ui={ui}><a href={sectorPath(v.sector)} style={{ color: B.text }}>{v.sector}</a></Fact>}
          {v.country && <Fact k="Country" ui={ui}>{v.country.name}</Fact>}
          {site && <Fact k="Website" ui={ui}><a href={`https://${site}`} target="_blank" rel="noreferrer noopener nofollow" style={{ color: B.text }}>{site} ↗</a></Fact>}
          {v.published && <Fact k="Posted" ui={ui}>{fmtDay(v.published)}</Fact>}
          {v.discovered && <Fact k="Seen by ransomware.live" ui={ui}>{fmtDay(v.discovered)}</Fact>}
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: fs(12), color: B.textDim }}>Confidence <ConfidenceBadge confidence={data.confidence} ui={ui} /></span>
        </div>
        <StixBar apiKey={`victim:${vid}`} ui={ui} />
      </div>

      <div style={{ ...card(ui), padding: "12px 18px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, borderColor: "#f59e0b55" }}>
        This is the group's own claim, posted to pressure the victim into paying. Groups sometimes exaggerate, repost old breaches or name the wrong company, and the victim rarely confirms it.
        {g?.unreliable && <b style={{ color: "#f59e0b" }}> ransomware.live flags this group's claims as unreliable.</b>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 14 }}>
        <Box title="About the organisation" ui={ui}>
          {v.description
            ? <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.65 }}>{v.description}{v.descriptionGenerated && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 6 }}>Summary written by ransomware.live's AI from public information; it may be wrong.</div>}</div>
            : <div style={{ fontSize: fs(12), color: B.textDim }}>No description available.</div>}
        </Box>

        <Box title="Ransom demand" ui={ui}>
          <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6 }}>
            Not public. Groups negotiate privately and rarely post the amount, and leak-site data doesn't include it.
          </div>
          {g?.ransom?.chats > 0 && (
            <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: 10, lineHeight: 1.6 }}>
              {g.label} has {nf(g.ransom.chats)} leaked negotiation chat{g.ransom.chats === 1 ? "" : "s"} with other victims, showing what it typically demands.{" "}
              <a href={`${groupHref(g)}?tab=negotiations`} style={{ color: B.accent }}>See its negotiations →</a>
            </div>
          )}
        </Box>
      </div>

      {g && (
        <Box title={`About ${g.label}`} ui={ui} right={groupHref(g) && <a href={groupHref(g)} style={{ color: B.accent, textDecoration: "none", textTransform: "none", letterSpacing: 0, fontSize: fs(12) }}>Open profile →</a>}>
          {g.snippet && <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6, marginBottom: 10 }}>{g.snippet}</div>}
          <div style={{ display: "flex", gap: "6px 22px", flexWrap: "wrap", marginBottom: 10 }}>
            <Fact k="Claimed victims" ui={ui}>{nf(g.total)}</Fact>
            {g.since && <Fact k="Active since" ui={ui}>{fmtDay(g.since)}</Fact>}
            {g.raas && <Fact k="Model" ui={ui}>Ransomware-as-a-service</Fact>}
          </div>
          {g.topSectors.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", fontSize: fs(12) }}>
              <span style={{ color: B.textDim }}>Hits most:</span>
              {g.topSectors.map(s => <a key={s.name} href={sectorPath(s.name)} style={{ textDecoration: "none" }}><Chip ui={ui}>{s.name} · {nf(s.count)}</Chip></a>)}
            </div>
          )}
          {(g.ransom?.notes > 0 || g.ransom?.chats > 0) && (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12, fontSize: fs(12) }}>
              {g.ransom.notes > 0 && <a href={`${groupHref(g)}?tab=notes`} style={{ color: B.accent, textDecoration: "none" }}>Read its ransom note →</a>}
              {g.ransom.chats > 0 && <a href={`${groupHref(g)}?tab=negotiations`} style={{ color: B.accent, textDecoration: "none" }}>{nf(g.ransom.chats)} leaked negotiations →</a>}
            </div>
          )}
        </Box>
      )}

      <Box title="Where this comes from" ui={ui}>
        <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, marginBottom: 10 }}>
          ransomware.live watches ransomware leak sites and records each new post. The post itself is on the group's Tor site, so its address is shown as text only, never as a link: opening it means visiting a criminal site, and the post may host stolen data.
        </div>
        {v.leakPost ? <CopyText text={v.leakPost} ui={ui} /> : <div style={{ fontSize: fs(12), color: B.textDim }}>No leak-site address recorded.</div>}
      </Box>

      {elsewhere.length > 0 && (
        <Box title="Also claimed" ui={ui}>
          <div style={{ fontSize: fs(12), color: B.textDim, marginBottom: 4 }}>Other posts naming the same organisation or website: a repost, a second attack, or another group reselling the data.</div>
          {elsewhere.map(x => <VictimLine key={x.vid} v={x} ui={ui} showGroup />)}
        </Box>
      )}

      {groupRecent.length > 0 && (
        <Box title={`${g?.label || "This group"}'s other recent claims`} ui={ui}>
          {groupRecent.map(x => <VictimLine key={x.vid} v={x} ui={ui} />)}
        </Box>
      )}

      <div style={{ fontSize: fs(11), color: B.textDim }}>From ransomware.live{data.builtAt ? `, updated ${ago(data.builtAt)}` : ""}.</div>
    </div>
  );
}

export function KbSector({ name, ui }) {
  const { B, fs, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/sector/${encodeURIComponent(name)}`);
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ color: "#dc2626", fontSize: fs(13) }}>{error}</div>;
  const max = Math.max(1, ...data.groups.map(g => g.last365));
  const color = getConf("intrusion-set").color;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/victims" style={{ color: B.textDim, textDecoration: "none" }}>Ransomware Victims</a> / Sectors</div>
      <div>
        <h2 style={{ fontSize: fs(24), fontWeight: 900, margin: "0 0 4px", color: B.text }}>{data.name} sector</h2>
        <div style={{ fontSize: fs(13), color: B.textMuted }}>
          {nf(data.total)} claimed ransomware victims · {nf(data.last365)} in the last year · {nf(data.last30)} in the last 30 days
        </div>
        <StixBar apiKey={`sector:${name}`} ui={ui} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))", gap: 14 }}>
        <Box title="Who targets it (last 12 months)" ui={ui}>
          {data.groups.filter(g => g.last365).slice(0, 15).map(g => (
            <div key={g.label} style={{ display: "flex", gap: 10, alignItems: "center", padding: "5px 0", fontSize: fs(12) }}>
              <span style={{ width: 150, flexShrink: 0, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{groupHref(g) ? <a href={groupHref(g)} style={{ color: B.text, textDecoration: "none", fontWeight: 700 }}>{g.label}</a> : g.label}</span>
              <span style={{ flex: 1, height: 8, background: B.bg, borderRadius: 4, overflow: "hidden" }}><span style={{ display: "block", height: "100%", width: `${(g.last365 / max) * 100}%`, background: color, borderRadius: 4 }} /></span>
              <span style={{ width: 70, textAlign: "right", color: B.textMuted, whiteSpace: "nowrap" }}>{nf(g.last365)}</span>
            </div>
          ))}
          {!data.groups.some(g => g.last365) && <div style={{ fontSize: fs(12), color: B.textDim }}>No claims in the last year.</div>}
          <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8 }}>Claimed victims in this sector per group. All-time leaders: {data.groups.slice().sort((a, b) => b.count - a.count).slice(0, 3).map(g => `${g.label} (${nf(g.count)})`).join(", ")}.</div>
        </Box>
        <Box title="Where its victims are" ui={ui}>
          {data.countries.map(c => (
            <div key={c.iso2} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: fs(12), borderBottom: `1px solid ${B.border}` }}>
              <span style={{ color: B.text }}>{c.name}</span><span style={{ color: B.textMuted }}>{nf(c.count)}</span>
            </div>
          ))}
        </Box>
      </div>
      <div style={{ fontSize: fs(12) }}>
        <a href="#/kb/victims" style={{ color: B.accent, textDecoration: "none" }}>Browse all victims, filtered by sector →</a>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", fontSize: fs(12) }}>
        <span style={{ color: B.textDim }}>Other sectors:</span>
        {data.allSectors.filter(s => s !== data.name && s !== "Unknown").map(s => <a key={s} href={sectorPath(s)} style={{ textDecoration: "none" }}><Chip ui={ui}>{s}</Chip></a>)}
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim }}>From ransomware.live leak-site claims{data.builtAt ? `, updated ${ago(data.builtAt)}` : ""}.</div>
    </div>
  );
}

// ─── Profile tabs ───

export function RansomNotes({ id, ui }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson(`/api/kb/ransom/${encodeURIComponent(id)}/notes`);
  const [i, setI] = useState(0);
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ color: "#dc2626", fontSize: fs(13) }}>{error}</div>;
  const notes = data.notes.filter(n => n.text);
  const note = notes[Math.min(i, notes.length - 1)];
  return (
    <>
      <p style={{ fontSize: fs(12), color: B.textMuted, margin: "0 0 12px", lineHeight: 1.6, maxWidth: "110ch" }}>
        The note this group's ransomware leaves on encrypted machines, from Zscaler ThreatLabz's archive. Each victim's copy has its own ID, and the wording changes between versions. Contact addresses in it are the group's; don't contact them.
      </p>
      {notes.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {notes.map((n, k) => <button key={n.name} onClick={() => setI(k)} style={{ fontSize: fs(12), fontFamily: "monospace", color: k === i ? B.text : B.textMuted, background: k === i ? B.primary + "18" : B.bgCard, border: `1px solid ${k === i ? B.primary + "55" : B.border}`, borderRadius: 20, padding: "5px 12px", cursor: "pointer" }}>{n.name}</button>)}
        </div>
      )}
      {note ? (
        <div style={{ ...card(ui), overflow: "hidden" }}>
          <pre style={{ margin: 0, padding: "16px 18px", fontSize: fs(12), lineHeight: 1.6, color: B.text, background: B.bg, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 640, overflowY: "auto" }}>{note.text}</pre>
          {note.truncated && <div style={{ fontSize: fs(11), color: B.textDim, padding: "8px 18px" }}>Shortened; the full note is in the archive.</div>}
        </div>
      ) : <div style={{ fontSize: fs(12), color: B.textDim }}>The notes couldn't be loaded right now.</div>}
      <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 10 }}><a href={data.source} target="_blank" rel="noreferrer noopener" style={{ color: B.textDim }}>ThreatLabz ransomware notes archive ↗</a></div>
    </>
  );
}

function ChatView({ id, chat, ui, onClose }) {
  const { B, fs, HP } = ui;
  const { data, error, loading } = useJson(`/api/kb/ransom/${encodeURIComponent(id)}/chat/${encodeURIComponent(chat)}`);
  return (
    <div style={{ ...card(ui), padding: "14px 16px", marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={sectionLabel(ui)}>Chat {chat}</span>
        <div style={{ display: "flex", gap: 8 }}>
          {data?.source && <a href={data.source} target="_blank" rel="noreferrer noopener" style={{ ...HP.navBtn, padding: "5px 11px", fontSize: fs(11), textDecoration: "none" }}>Source ↗</a>}
          <button onClick={onClose} style={{ ...HP.navBtn, padding: "5px 11px", fontSize: fs(11) }}>Close</button>
        </div>
      </div>
      {loading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Loading…</div>}
      {error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 620, overflowY: "auto" }}>
        {data?.messages.map((m, k) => {
          const victim = /^victim$/i.test(m.party);
          return (
            <div key={k} style={{ alignSelf: victim ? "flex-end" : "flex-start", maxWidth: "min(85%, 720px)", background: victim ? B.primary + "14" : B.bg, border: `1px solid ${victim ? B.primary + "44" : B.border}`, borderRadius: 10, padding: "8px 12px" }}>
              <div style={{ fontSize: fs(10), color: victim ? B.accent : "#dc2626", fontWeight: 800, marginBottom: 3, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                {victim ? "Victim" : m.party}
                {m.time && <span style={{ color: B.textDim, fontWeight: 500 }}>{m.time}</span>}
                {m.amounts.map((a, j) => <Chip key={j} ui={ui} color={victim ? B.accent : "#dc2626"}>{a.usd ? money(a.usd) : `${a.crypto} ${a.unit}`}</Chip>)}
              </div>
              <div style={{ fontSize: fs(12), color: B.textMuted, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.55 }}>{m.text}</div>
              {m.wallets?.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                  {m.wallets.map(w => (
                    <a key={w.address} href={walletPath(w.chain, w.address)} title="Open this wallet: what it received, and where the money went" style={{ fontSize: fs(11), fontWeight: 700, color: B.accent, textDecoration: "none", border: `1px solid ${B.accent}66`, borderRadius: 6, padding: "2px 8px" }}>
                      Follow this wallet → {w.address.slice(0, 10)}…
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Negotiations({ id, ui }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson(`/api/kb/ransom/${encodeURIComponent(id)}/chats`);
  const [open, setOpen] = useState(null);
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading the chats… (first load takes a few seconds)</div>;
  if (error) return <div style={{ color: "#dc2626", fontSize: fs(13) }}>{error}</div>;
  const withDemand = data.chats.filter(c => c.firstDemand);
  return (
    <>
      <p style={{ fontSize: fs(12), color: B.textMuted, margin: "0 0 12px", lineHeight: 1.6, maxWidth: "110ch" }}>
        Real negotiations between {data.group} and its victims, leaked or shared by researchers, with the victims' names removed. Amounts are picked out of the messages automatically, so open a chat to read them in context.
      </p>
      {withDemand.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
          {[["Typical first demand", money(data.medianDemand)], ["Lowest first demand", money(data.minDemand)], ["Highest first demand", money(data.maxDemand)], ["Chats with an amount", `${withDemand.length} of ${data.chats.length}`]].map(([k, v]) => (
            <div key={k} style={{ ...card(ui), padding: "12px 14px" }}>
              <div style={{ fontSize: fs(20), fontWeight: 900, color: B.text }}>{v}</div>
              <div style={{ fontSize: fs(11), color: B.textDim }}>{k}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ ...card(ui), overflow: "hidden" }}>
        {data.chats.map((c, k) => (
          <button key={c.id} onClick={() => setOpen(open === c.id ? null : c.id)} style={{ display: "flex", gap: 12, width: "100%", textAlign: "left", alignItems: "baseline", flexWrap: "wrap", padding: "10px 16px", background: open === c.id ? B.primary + "12" : "transparent", border: "none", borderTop: k ? `1px solid ${B.border}` : "none", cursor: "pointer", fontFamily: "'Satoshi', sans-serif", color: B.text, fontSize: fs(12) }}>
            <span style={{ fontWeight: 700, minWidth: 110 }}>{c.date ? fmtDay(c.date) : `Chat ${c.id}`}</span>
            <span style={{ color: B.textDim }}>{nf(c.messages)} messages</span>
            <span style={{ flex: 1 }} />
            {c.firstDemand && <span style={{ color: B.textMuted }}>asked {money(c.firstDemand)}{c.lastDemand && c.lastDemand !== c.firstDemand ? ` → ${money(c.lastDemand)}` : ""}</span>}
            {c.lastOffer && <span style={{ color: B.textDim }}>victim offered {money(c.lastOffer)}</span>}
          </button>
        ))}
      </div>
      {open && <ChatView id={id} chat={open} ui={ui} onClose={() => setOpen(null)} />}
      <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 10 }}>{data.total > data.chats.length ? `Showing the latest ${data.chats.length} of ${data.total}. ` : ""}<a href={data.source} target="_blank" rel="noreferrer noopener" style={{ color: B.textDim }}>Ransomchats archive ↗</a></div>
    </>
  );
}
